import { useState, useRef, useEffect } from "react";
import ActivityPlayer, { ActivityPlayerHandle } from "./ActivityPlayer";
import { haptic } from "@/lib/haptic";
import { SentenceActivity } from "@shared_types";
import { IoVolumeMedium } from "react-icons/io5";
import { MdKeyboardDoubleArrowUp } from "react-icons/md";
import WhiteActivityButton from "../Buttons/WhiteActivityButton";
import ClearButton from "../Buttons/ClearButton";
import WordButton3D from "../Buttons/WordButton3D";
import ActivityFixButton from "../Buttons/ActivtyFixButton";
import { callWithAuth, postWordError } from "@/api/Api";

const isSmallScreen = () => {
  return window.innerHeight <= 770;
};

const isMaxWidth = () => {
  return window.innerWidth >= 590;
};

interface SelectedOption {
  id: number;
  word: string;
  selectionOrder: number;
}

interface SentanceProps {
  sentenceActivity: SentenceActivity;
  onActivityFinished: () => void;
  onTrackListeningTime: (time: number) => void;
  onSkip?: () => void;
  lessonId: number;
  validWordId: number;
}

const Sentence: React.FC<SentanceProps> = ({
  sentenceActivity,
  onActivityFinished,
  onTrackListeningTime,
  onSkip,
  lessonId,
  validWordId,
}) => {
  const [selectedOptions, setSelectedOptions] = useState<SelectedOption[]>([]);
  const [timesIncorrect, setTimesIncorrect] = useState(0);
  const [showListenButton, setShowListenButton] = useState(
    !isSmallScreen() && !isMaxWidth()
  );
  const [showResult, setShowResult] = useState(false);
  const [isAnswerCorrect, setIsAnswerCorrect] = useState(false);
  const videoRef = useRef<ActivityPlayerHandle>(null);

  // Update showListenButton when screen size changes
  useEffect(() => {
    const handleResize = () => {
      setShowListenButton(!isSmallScreen() && !isMaxWidth());
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const displayWords = sentenceActivity.userVideoWords.map(
    (word) => word.displayText
  );

  const answerWords = displayWords.map((word) => normalizeText(word));

  const [options] = useState(() =>
    [...displayWords].sort(() => Math.random() - 0.5)
  );

  const handleCorrect = () => {
    haptic.success();
    setShowResult(true);
    setIsAnswerCorrect(true);
    setTimeout(() => {
      onActivityFinished();
    }, 500);
  };

  const handleIncorrect = () => {
    haptic.error();
    setTimesIncorrect((prev) => prev + 1);
    setShowResult(true);
    setIsAnswerCorrect(false);
    setTimeout(() => {
      setSelectedOptions([]);
      setShowResult(false);
      setIsAnswerCorrect(false);
    }, 1000);
  };

  const handleOptionClick = (word: string, index: number) => {
    if (selectedOptions.some((selected) => selected.id === index)) {
      setSelectedOptions((prev) => {
        const filtered = prev.filter((item) => item.id !== index);
        return filtered.map((item, newIndex) => ({
          ...item,
          selectionOrder: newIndex,
        }));
      });
    } else {
      setTimeout(() => {
        setSelectedOptions((prev) => {
          const newSelectedOptions = [
            ...prev,
            { id: index, word, selectionOrder: prev.length },
          ];

          if (newSelectedOptions.length === answerWords.length) {
            const currentWords = newSelectedOptions
              .sort((a, b) => a.selectionOrder - b.selectionOrder)
              .map((option) => normalizeText(option.word));

            if (currentWords.every((word, i) => word === answerWords[i])) {
              handleCorrect();
            } else {
              handleIncorrect();
            }
          }

          return newSelectedOptions;
        });
      });
    }
  };

  const handleWordRemove = (wordIndex: number) => {
    haptic.selection();
    const selectedWords = selectedOptions
      .sort((a, b) => a.selectionOrder - b.selectionOrder)
      .map((option) => option.word);

    const wordToRemove = selectedWords[wordIndex];
    if (wordToRemove) {
      const optionToRemove = selectedOptions.find(
        (opt) => opt.word === wordToRemove
      );
      if (optionToRemove) {
        setTimeout(() => {
          handleOptionClick(wordToRemove, optionToRemove.id);
        }, 300);
      }
    }
  };

  const handleClear = () => {
    haptic.soft();
    setSelectedOptions([]);
    setShowResult(false);
    setIsAnswerCorrect(false);
  };

  const handleActivityFix = () => {
    haptic.soft();
    setSelectedOptions([]);
    setShowResult(false);
    setIsAnswerCorrect(false);
    callWithAuth(postWordError, {
      videoWordId: validWordId,
      feedback:
        "SENTANCE words are missing -- answer words:" + displayWords.join(" "),
      lessonId: lessonId,
    });
    // Automatically move to next activity since this one is broken
    setTimeout(() => {
      if (onSkip) {
        onSkip();
      } else {
        onActivityFinished();
      }
    }, 200);
  };

  const handleListenToAudio = () => {
    if (videoRef.current) {
      videoRef.current.triggerOverlayClick();
    }
  };

  const selectedWords = selectedOptions
    .sort((a, b) => a.selectionOrder - b.selectionOrder)
    .map((option) => option.word);

  const selectedOptionIds = new Set(selectedOptions.map((opt) => opt.id));

  return (
    <div
      className={`appPadding pt-4 flex flex-col items-center w-full justify-between h-full ${
        isSmallScreen() ? "gap-1" : "gap-3"
      }`}
    >
      <div
        className={`w-full flex flex-col justify-start ${
          isSmallScreen() ? "gap-1" : "gap-4"
        }`}
      >
        <div className="w-full text-xl font-bold">
          Listen and write the sentence
        </div>
        <div className="bg-white rounded-xl shadow-md w-full overflow-hidden">
          <ActivityPlayer
            ref={videoRef}
            youtubeId={sentenceActivity.youtubeId}
            onTimeUpdate={() => {}}
            startTime={sentenceActivity.start}
            endTime={sentenceActivity.end}
            onTrackListeningTime={onTrackListeningTime}
            onPlay={() => {}}
          />
        </div>

        {showListenButton && (
          <WhiteActivityButton
            leftIcon={
              <IoVolumeMedium color="var(--color-blue)" className="w-6 h-6" />
            }
            onClick={handleListenToAudio}
          >
            Listen to audio
          </WhiteActivityButton>
        )}

        <div className="w-full flex flex-col items-center gap-2 mt-2 mb-2">
          <div className="w-[95%] min-h-[120px] flex flex-col justify-center">
            <div className="relative">
              <div className="flex flex-col gap-12 text-lg pt-12">
                <div className="w-full h-[2px] bg-[var(--color-grey-light)]"></div>
                <div className="w-full h-[2px] bg-[var(--color-grey-light)]"></div>
                {!isMaxWidth() && (
                  <div className="w-full h-[2px] bg-[var(--color-grey-light)]"></div>
                )}
              </div>
              <div className="absolute left-0 right-0 top-0 flex flex-wrap items-start gap-y-2">
                {selectedWords.map((word, wordIndex) => (
                  <WordButton3D
                    key={wordIndex}
                    word={word}
                    index={wordIndex}
                    onClick={() => handleWordRemove(wordIndex)}
                    isCorrect={isAnswerCorrect}
                    showResult={showResult}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="w-full flex flex-wrap gap-1 justify-center">
        {options.map((word, idx) => (
          <WordButton3D
            key={idx}
            word={word}
            index={idx}
            onClick={handleOptionClick}
            disabled={selectedOptionIds.has(idx)}
          />
        ))}
      </div>
      {timesIncorrect >= 2 && (
        <div className="text-[var(--color-orange)] text-sm">
          pstt... it's - "{displayWords.join(" ")}"
        </div>
      )}
      <div className="h-12 flex items-center justify-center gap-2">
        {selectedOptions.length > 0 ? (
          <>
            <ClearButton onClick={handleClear} />
            <ActivityFixButton onClick={handleActivityFix} />
          </>
        ) : (
          <div className="w-full flex flex-col items-center justify-center">
            <MdKeyboardDoubleArrowUp className="w-7 h-auto" />
            <span className="text-[11px]">Swipe up to skip</span>
          </div>
        )}
      </div>
    </div>
  );
};

const normalizeText = (text: string): string => {
  return text
    .toLowerCase()
    .replace(/[^\w\s]|_/g, "")
    .replace(/\s+/g, " ");
};

export default Sentence;
